import { z } from 'zod';

import { BILLING_MODELS, PRICING_MODES } from '../finance/engine-types';
import { MAX_MONEY_CENTS, MAX_RATE_CENTS } from '../finance/money';

/**
 * Finance contracts (docs/release-1/18-COST-AND-FINANCE-ANALYTICS.md §5).
 * Every route is a browser JWT session of a finance viewer (OWNER / MANAGER,
 * or a custom role with `time.view_billing` not derived from OUTSTAFF /
 * VIEWER). Amounts are integers in minor units; the server never parses
 * money strings. No key starts with `plan` (doc 17 overlay deny-list).
 */

export const MoneyCentsSchema = z.number().int().min(1).max(MAX_MONEY_CENTS);
export const RateCentsSchema = z.number().int().min(1).max(MAX_RATE_CENTS);
export const CurrencySchema = z.string().regex(/^[A-Z]{3}$/);
export const ApplyRateFromSchema = z.enum(['today', 'all']);
export type ApplyRateFrom = z.infer<typeof ApplyRateFromSchema>;
const DaySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const SignedCents = z.number().int();

export const BillingModelSchema = z.enum(BILLING_MODELS);
export const PricingModeSchema = z.enum(PRICING_MODES);
export const RateSourceSchema = z.enum(['task', 'parent', 'stage', 'project', 'team']);

// ── Access ─────────────────────────────────────────────────────────────────
export const FinanceAccessSchema = z
  .object({
    teams: z.array(
      z.object({ teamId: z.string().uuid(), teamSlug: z.string(), canView: z.boolean(), canEdit: z.boolean() }).strict(),
    ),
  })
  .strict();
export type FinanceAccessList = z.infer<typeof FinanceAccessSchema>;

// ── Team defaults (currency + default rate) ───────────────────────────────
export const TeamFinanceSettingsSchema = z
  .object({
    teamId: z.string().uuid(),
    currency: CurrencySchema,
    defaultRateCents: RateCentsSchema.nullable(),
    canEdit: z.boolean(),
  })
  .strict();
export type TeamFinanceSettings = z.infer<typeof TeamFinanceSettingsSchema>;

export const UpdateTeamFinanceSchema = z
  .object({
    currency: CurrencySchema.optional(),
    defaultRateCents: RateCentsSchema.nullable().optional(),
    applyRateFrom: ApplyRateFromSchema.default('today'),
  })
  .strict();
export type UpdateTeamFinanceInput = z.infer<typeof UpdateTeamFinanceSchema>;

// ── Project money settings ─────────────────────────────────────────────────
export const FinanceSnapshotKindSchema = z.enum(['baseline', 'invoiced']);
export const FinanceSnapshotSchema = z
  .object({
    kind: FinanceSnapshotKindSchema,
    asOf: DaySchema,
    totalCents: z.number().int().min(0),
    note: z.string().nullable(),
    createdAt: z.string(),
  })
  .strict();
export type FinanceSnapshot = z.infer<typeof FinanceSnapshotSchema>;

export const ProjectFinanceSettingsSchema = z
  .object({
    tracking: z.boolean(),
    billingModel: BillingModelSchema,
    /** Effective currency (project override ?? team default). */
    currency: CurrencySchema,
    /** null = "Using team default". */
    currencyOverride: CurrencySchema.nullable(),
    teamCurrency: CurrencySchema,
    /** The project's own rate; null = "Using team default". */
    rateCents: RateCentsSchema.nullable(),
    teamRateCents: RateCentsSchema.nullable(),
    budgetCents: MoneyCentsSchema.nullable(),
    budgetHours: z.number().nullable(),
    clientName: z.string().nullable(),
    trustDefaultEstimates: z.boolean(),
    invoicedThrough: FinanceSnapshotSchema.nullable(),
    baseline: FinanceSnapshotSchema.nullable(),
    hasLoggedTime: z.boolean(),
  })
  .strict();
export type ProjectFinanceSettings = z.infer<typeof ProjectFinanceSettingsSchema>;

export const UpdateProjectFinanceSchema = z
  .object({
    tracking: z.boolean().optional(),
    billingModel: BillingModelSchema.optional(),
    /** null = back to the team default. */
    currency: CurrencySchema.nullable().optional(),
    rateCents: RateCentsSchema.nullable().optional(),
    applyRateFrom: ApplyRateFromSchema.default('today'),
    budgetCents: MoneyCentsSchema.nullable().optional(),
    clientName: z.string().trim().min(1).max(120).nullable().optional(),
    trustDefaultEstimates: z.boolean().optional(),
    confirmCurrencyChange: z.boolean().optional(),
    confirmModelChange: z.boolean().optional(),
  })
  .strict();
export type UpdateProjectFinanceInput = z.infer<typeof UpdateProjectFinanceSchema>;

export const SetFinanceSnapshotSchema = z
  .object({
    kind: FinanceSnapshotKindSchema,
    asOf: DaySchema,
    note: z.string().trim().max(200).nullable().default(null),
  })
  .strict();
export type SetFinanceSnapshotInput = z.infer<typeof SetFinanceSnapshotSchema>;

// ── Task / milestone pricing ───────────────────────────────────────────────
export const SetTaskPricingSchema = z
  .object({
    /** null = project default (deletes the row). */
    mode: PricingModeSchema.nullable(),
    rateCents: RateCentsSchema.nullable().default(null),
    priceCents: MoneyCentsSchema.nullable().default(null),
    applyRateFrom: ApplyRateFromSchema.default('today'),
  })
  .strict()
  .superRefine((v, ctx) => {
    const bad = (path: string, message: string) => ctx.addIssue({ code: 'custom', path: [path], message });
    if ((v.mode === null || v.mode === 'none') && (v.rateCents !== null || v.priceCents !== null)) {
      bad('mode', 'pricing_shape_invalid: no amounts for project default / not billed');
    }
    if (v.mode === 'hourly' && v.priceCents !== null) bad('priceCents', 'pricing_shape_invalid: hourly has no price');
    if (v.mode === 'fixed' && v.priceCents === null) bad('priceCents', 'pricing_shape_invalid: fixed needs a price');
    if (v.mode === 'fixed' && v.rateCents !== null) bad('rateCents', 'pricing_shape_invalid: fixed has no own rate');
  });
export type SetTaskPricingInput = z.infer<typeof SetTaskPricingSchema>;

export const SetMilestonePricingSchema = z
  .object({
    /** Both null = delete the row (sum of tasks, inherited rate). */
    priceCents: MoneyCentsSchema.nullable(),
    rateCents: RateCentsSchema.nullable(),
    applyRateFrom: ApplyRateFromSchema.default('today'),
  })
  .strict();
export type SetMilestonePricingInput = z.infer<typeof SetMilestonePricingSchema>;

// ── Read models ────────────────────────────────────────────────────────────
export const FINANCE_FLAGS = [
  'no_rate',
  'no_estimate',
  'default_estimate',
  'no_target_rate',
  'over_estimate',
  'below_target_rate',
  'contained',
] as const;
export const FinanceFlagSchema = z.enum(FINANCE_FLAGS);

export const ContainerSchema = z
  .object({
    kind: z.enum(['stage', 'task', 'project']),
    id: z.string().uuid(),
    name: z.string(),
    priceCents: z.number().int().nullable(),
  })
  .strict();

export const TaskFinanceSchema = z
  .object({
    taskId: z.string().uuid(),
    projectId: z.string().uuid(),
    tracking: z.boolean(),
    currency: CurrencySchema,
    billingModel: BillingModelSchema,
    explicitMode: PricingModeSchema.nullable(),
    mode: PricingModeSchema,
    ownRateCents: RateCentsSchema.nullable(),
    rateCents: z.number().int().nullable(),
    rateSource: RateSourceSchema.nullable(),
    rateSourceLabel: z.string().nullable(),
    priceCents: z.number().int().nullable(),
    estimateHours: z.number().min(0),
    defaultEstimate: z.boolean(),
    loggedSeconds: z.number().int().min(0),
    billableSeconds: z.number().int().min(0),
    expectedCents: SignedCents.nullable(),
    earnedCents: SignedCents.nullable(),
    toCompleteCents: SignedCents.nullable(),
    effortCents: SignedCents.nullable(),
    targetRateCents: SignedCents.nullable(),
    effectiveRateCents: SignedCents.nullable(),
    vsTargetCents: SignedCents.nullable(),
    container: ContainerSchema.nullable(),
    flags: z.array(FinanceFlagSchema),
    canEdit: z.boolean(),
  })
  .strict();
export type TaskFinance = z.infer<typeof TaskFinanceSchema>;

export const TaskFinanceRowSchema = z
  .object({
    taskId: z.string().uuid(),
    key: z.string(),
    title: z.string(),
    statusCategory: z.string(),
    assigneeId: z.string().uuid().nullable(),
    milestoneId: z.string().uuid().nullable(),
    mode: PricingModeSchema,
    explicitMode: PricingModeSchema.nullable(),
    rateCents: z.number().int().nullable(),
    rateSource: RateSourceSchema.nullable(),
    priceCents: z.number().int().nullable(),
    estimateHours: z.number(),
    loggedSeconds: z.number().int(),
    expectedCents: SignedCents.nullable(),
    earnedCents: SignedCents.nullable(),
    toCompleteCents: SignedCents.nullable(),
    effectiveRateCents: SignedCents.nullable(),
    vsTargetCents: SignedCents.nullable(),
    contained: z.boolean(),
    flags: z.array(FinanceFlagSchema),
  })
  .strict();
export type TaskFinanceRow = z.infer<typeof TaskFinanceRowSchema>;

export const MilestoneFinanceSchema = z
  .object({
    milestoneId: z.string().uuid().nullable(),
    name: z.string(),
    kind: z.enum(['fixed', 'rollup']),
    priceCents: z.number().int().nullable(),
    ownRateCents: z.number().int().nullable(),
    rateCents: z.number().int().nullable(),
    expectedCents: SignedCents,
    breakdownCents: SignedCents,
    earnedCents: SignedCents,
    toCompleteCents: SignedCents,
    forecastCents: SignedCents,
    loggedSeconds: z.number().int(),
    effortCents: SignedCents.nullable(),
    effectiveRateCents: SignedCents.nullable(),
    targetRateCents: SignedCents.nullable(),
    vsTargetCents: SignedCents.nullable(),
    taskCount: z.number().int(),
    isCompleted: z.boolean(),
    readyToComplete: z.boolean(),
  })
  .strict();
export type MilestoneFinance = z.infer<typeof MilestoneFinanceSchema>;

export const CHECK_CODES = [
  'stage_ready_to_complete',
  'changed_after_invoicing',
  'parent_and_children_estimated',
  'default_estimate',
  'no_rate',
  'done_without_time',
  'no_target_rate',
  'conflicting_containers',
  'breakdown_mismatch',
  'no_fee',
  'fee_mismatch',
] as const;
export const CheckItemSchema = z
  .object({
    code: z.enum(CHECK_CODES),
    count: z.number().int(),
    /** Money at stake (sort key), or seconds for `no_rate`. */
    cents: SignedCents.nullable(),
    seconds: z.number().int().nullable(),
    refs: z.array(z.object({ kind: z.enum(['task', 'stage', 'project']), id: z.string(), label: z.string() }).strict()),
  })
  .strict();
export type CheckItem = z.infer<typeof CheckItemSchema>;

export const FinanceKpisSchema = z
  .object({
    earnedToDateCents: SignedCents,
    expectedCents: SignedCents,
    toCompleteCents: SignedCents,
    forecastCents: SignedCents,
    budgetCents: z.number().int().nullable(),
    budgetUsedPct: z.number().nullable(),
    budgetVarianceCents: SignedCents.nullable(),
    unbilledCents: SignedCents.nullable(),
    changedAfterInvoicingCents: SignedCents.nullable(),
    scopeChangeCents: SignedCents.nullable(),
    effortToDateCents: SignedCents,
    effortForecastCents: SignedCents,
    loggedSeconds: z.number().int(),
    billableSeconds: z.number().int(),
    noRateSeconds: z.number().int(),
    fixedWorkSeconds: z.number().int(),
    nonBillableSeconds: z.number().int(),
    hoursToCompleteSeconds: z.number().int(),
    feeEffectiveRateCents: SignedCents.nullable(),
    feeForecastRateCents: SignedCents.nullable(),
    targetRateCents: SignedCents.nullable(),
  })
  .strict();
export type FinanceKpis = z.infer<typeof FinanceKpisSchema>;

export const ProjectFinanceOverviewSchema = z
  .object({
    projectId: z.string().uuid(),
    today: DaySchema,
    settings: ProjectFinanceSettingsSchema,
    kpis: FinanceKpisSchema.nullable(),
    milestones: z.array(MilestoneFinanceSchema),
    checkData: z.array(CheckItemSchema),
    canEdit: z.boolean(),
  })
  .strict();
export type ProjectFinanceOverview = z.infer<typeof ProjectFinanceOverviewSchema>;

export const ProjectFinanceTasksSchema = z.object({ items: z.array(TaskFinanceRowSchema) }).strict();

export const ProjectFinanceReportQuerySchema = z
  .object({
    from: DaySchema.optional(),
    to: DaySchema.optional(),
    assigneeIds: z.string().optional(),
    milestoneId: z.string().uuid().optional(),
  })
  .strict();
export type ProjectFinanceReportQuery = z.infer<typeof ProjectFinanceReportQuerySchema>;

export const FinanceSeriesPointSchema = z
  .object({ start: DaySchema, end: DaySchema, earnedCents: SignedCents, cumulativeCents: SignedCents })
  .strict();
export const PersonFinanceSchema = z
  .object({
    userId: z.string().uuid().nullable(),
    seconds: z.number().int(),
    billableSeconds: z.number().int(),
    fixedWorkSeconds: z.number().int(),
    nonBillableSeconds: z.number().int(),
    earnedCents: SignedCents,
  })
  .strict();
export type PersonFinance = z.infer<typeof PersonFinanceSchema>;
export const FixedJobSchema = z
  .object({
    kind: z.enum(['stage', 'task', 'project']),
    id: z.string(),
    name: z.string(),
    priceCents: z.number().int().nullable(),
    seconds: z.number().int(),
    effectiveRateCents: SignedCents.nullable(),
    targetRateCents: SignedCents.nullable(),
    vsTargetCents: SignedCents.nullable(),
    completed: z.boolean(),
  })
  .strict();
export type FixedJob = z.infer<typeof FixedJobSchema>;

export const ProjectFinanceReportSchema = z
  .object({
    from: DaySchema,
    to: DaySchema,
    granularity: z.enum(['day', 'week', 'month']),
    currency: CurrencySchema,
    earnedCents: SignedCents,
    series: z.array(FinanceSeriesPointSchema),
    byPerson: z.array(PersonFinanceSchema),
    fixedJobs: z.array(FixedJobSchema),
    fixedWork: z
      .object({
        effectiveRateCents: SignedCents.nullable(),
        targetRateCents: SignedCents.nullable(),
        doneWithoutTimeCount: z.number().int(),
        doneWithoutTimeCents: SignedCents,
      })
      .strict(),
    cancelledEffort: z.object({ seconds: z.number().int(), earnedCents: SignedCents }).strict(),
  })
  .strict();
export type ProjectFinanceReport = z.infer<typeof ProjectFinanceReportSchema>;

export const FinanceExportQuerySchema = z.object({ from: DaySchema.optional(), to: DaySchema.optional() }).strict();
export type FinanceExportQuery = z.infer<typeof FinanceExportQuerySchema>;

// ── Team overview ──────────────────────────────────────────────────────────
export const TeamFinanceQuerySchema = z.object({ month: z.string().regex(/^\d{4}-\d{2}$/).optional() }).strict();
export type TeamFinanceQuery = z.infer<typeof TeamFinanceQuerySchema>;

export const CurrencyTotalSchema = z
  .object({ currency: CurrencySchema, earnedCents: SignedCents, unbilledCents: SignedCents })
  .strict();
export const TeamFinanceProjectSchema = z
  .object({
    projectId: z.string().uuid(),
    slug: z.string(),
    name: z.string(),
    clientName: z.string().nullable(),
    currency: CurrencySchema,
    billingModel: BillingModelSchema,
    archived: z.boolean(),
    earnedCents: SignedCents,
    unbilledCents: SignedCents.nullable(),
    forecastCents: SignedCents,
    budgetCents: z.number().int().nullable(),
    budgetVarianceCents: SignedCents.nullable(),
    loggedSeconds: z.number().int(),
    billableSeconds: z.number().int(),
  })
  .strict();
export type TeamFinanceProject = z.infer<typeof TeamFinanceProjectSchema>;
export const TeamFinanceAlertSchema = z
  .object({
    projectId: z.string().uuid(),
    projectSlug: z.string(),
    projectName: z.string(),
    code: z.enum(['over_budget', 'stage_ready_to_complete', 'changed_after_invoicing']),
    currency: CurrencySchema,
    cents: SignedCents,
    label: z.string().nullable(),
  })
  .strict();
export const TeamFinanceOverviewSchema = z
  .object({
    month: z.string(),
    settings: TeamFinanceSettingsSchema,
    totals: z.array(CurrencyTotalSchema),
    projects: z.array(TeamFinanceProjectSchema),
    alerts: z.array(TeamFinanceAlertSchema),
  })
  .strict();
export type TeamFinanceOverview = z.infer<typeof TeamFinanceOverviewSchema>;

// ── Change log ─────────────────────────────────────────────────────────────
export const FinanceHistoryItemSchema = z
  .object({
    id: z.string(),
    entity: z.enum(['team', 'project', 'milestone', 'task']),
    entityId: z.string().uuid(),
    entityLabel: z.string().nullable(),
    actorName: z.string().nullable(),
    source: z.string(),
    change: z.record(z.unknown()),
    createdAt: z.string(),
  })
  .strict();
export const FinanceHistoryPageSchema = z
  .object({ items: z.array(FinanceHistoryItemSchema), nextCursor: z.string().nullable() })
  .strict();
export type FinanceHistoryPage = z.infer<typeof FinanceHistoryPageSchema>;
export const FinanceHistoryQuerySchema = z.object({ cursor: z.string().regex(/^\d+$/).optional() }).strict();

/** `GET/PUT /api/milestones/:milestoneId/(finance|pricing)` — a stage card + context. */
export const MilestoneFinanceViewSchema = MilestoneFinanceSchema.extend({
  currency: CurrencySchema,
  tracking: z.boolean(),
  canEdit: z.boolean(),
}).strict();
export type MilestoneFinanceView = z.infer<typeof MilestoneFinanceViewSchema>;
